import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import useUpdateEffect from '../../../Hooks/useUpdateEffect';

// COMPONENTS
import Field from './Field';

// STYLES
import style from '../../../../scss/components/form.module.scss';
import '../../../../scss/external/select.scss';

const AutoComplete = ({
    onChange,
    entity,
    placeholder,
    searchBy,
    isMultiple = false,
    withoutId,
    disableFallback,
    ignoreOption,
    ...props
}) => {
    const { name, service, reference, allowNew } = props;
    const { t } = useTranslation();

    const isIgnoredByName = (ignoreOption, preSelectedValue) => {
        if (!ignoreOption) {
            return false;
        }

        const needle = String(ignoreOption).trim().toLowerCase();
        return String(preSelectedValue).trim().toLowerCase() === needle;
    };

    const [state, setState] = useState({
        items: [],
        selected: entity[name]
            ? (Array.isArray(entity[name]) && entity[name]) || [entity[name]]
            : [],
        isLoading: false,
    });

    const update = (change) =>
        setState((oldState) => ({ ...oldState, ...change }));

    const hasIgnoredSelected = useMemo(() => {
        return state.selected.some((item) =>
            isIgnoredByName(ignoreOption, item?.name),
        );
    }, [state.selected, ignoreOption]);

    const onSearch = (query) => {
        if (hasIgnoredSelected) {
            return;
        }

        update({ isLoading: true });

        service
            .autocomplete(reference, {
                [searchBy]: query,
                withoutId: withoutId || (disableFallback ? null : entity.id),
            })
            .then((result) => {
                const items = [...result.data];

                update({ items, isLoading: false });
            });
    };

    const onSelectionChange = (selection) => {
        let nextSelection = selection;

        if (isMultiple && Array.isArray(nextSelection)) {
            const ignoredItem = nextSelection.find((item) =>
                isIgnoredByName(ignoreOption, item?.name),
            );

            if (ignoredItem) {
                nextSelection = [ignoredItem];
            }
        }

        update({
            selected: nextSelection,
            items:
                Array.isArray(nextSelection) &&
                nextSelection.some((item) =>
                    isIgnoredByName(ignoreOption, item?.name),
                )
                    ? []
                    : state.items,
        });
    };

    useUpdateEffect(() => {
        if (isMultiple) {
            onChange({ [name]: state.selected });
        } else {
            onChange({ [name]: state.selected[0] || null });
        }
    }, [state.selected]);

    return (
        <div className={classNames(style.formGroup, props.className)}>
            <Field {...props}>
                {(hasError, attributes, isChanged) => {
                    return (
                        <AsyncTypeahead
                            id={`${entity.name}.${name}-select`}
                            className={`
                                ${style.autocomplete}
                                -${name}
                                ${isChanged ? style.changedInput : ''}
                            `}
                            isLoading={state.isLoading}
                            onSearch={onSearch}
                            promptText={placeholder}
                            minLength={props.minLength}
                            multiple={isMultiple}
                            labelKey={(option) => {
                                if (!option) return '[No label]';
                                return (
                                    option[searchBy] ||
                                    option.title ||
                                    option.name ||
                                    '[No label]'
                                );
                            }}
                            options={hasIgnoredSelected ? [] : state.items}
                            selected={state.selected}
                            flip
                            emptyLabel={t('application.noResults')}
                            searchText={t('application.search')}
                            onChange={onSelectionChange}
                            inputProps={{
                                id: attributes.id,
                                readOnly: hasIgnoredSelected,
                                className: `
                                    ${style.textArea}
                                    ${hasError ? style.isInvalid : ''}
                                `,
                            }}
                            allowNew={hasIgnoredSelected ? false : allowNew}
                            {...attributes}
                        />
                    );
                }}
            </Field>
        </div>
    );
};

export default AutoComplete;

AutoComplete.propTypes = {
    /**
     * Type of entity
     */
    entity: PropTypes.object.isRequired,
    /**
     * Type of onChange
     */
    onChange: PropTypes.func.isRequired,
    /**
     * Type of placeholder
     */
    placeholder: PropTypes.string.isRequired,
    /**
     * Type of searchBy
     */
    searchBy: PropTypes.string.isRequired,
    /**
     * Type of service
     */
    service: PropTypes.object.isRequired,
    /**
     * Type of reference
     */
    reference: PropTypes.string.isRequired,
    /**
     * Type of isMultiple
     */
    isMultiple: PropTypes.bool.isRequired,
    /**
     * Type of name
     */
    name: PropTypes.string.isRequired,
    /**
     * Type of allowNew
     */
    allowNew: PropTypes.bool,
    /**
     * Type of withoutId
     */
    withoutId: PropTypes.number,
    /**
     * Type of disableFallback
     */
    disableFallback: PropTypes.bool,
    /**
     * Type of minLeghth
     */
    minLength: PropTypes.number,
    ignoreOption: PropTypes.string,
};

AutoComplete.defaultProps = {
    allowNew: false,
    withoutId: null,
    disableFallback: false,
    minLength: 3,
    ignoreOption: '',
};
